import React, { useState } from 'react';
import { X, Plus, Gift, Check, Trash2 } from 'lucide-react';

export default function HabitRewards({ habit, logs, onUpdate }) {
  const [showAdd, setShowAdd] = useState(false);
  const [ruleType, setRuleType] = useState('weekly_rate');
  const [threshold, setThreshold] = useState(5);
  const [rewardText, setRewardText] = useState('');
  const [unlockedReward, setUnlockedReward] = useState(null);

  const rules = habit.rewards?.bigRewardRules || [];

  const getProgress = (rule) => {
    const today = new Date();
    if (rule.ruleType === 'weekly_rate') {
      // Count done in current week (Mo-So)
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      let done = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (logs.some(l => l.date === dateStr && l.done)) done++;
      }
      return { current: done, target: rule.threshold };
    }
    if (rule.ruleType === 'streak') {
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const doneDates = new Set(logs.filter(l => l.done).map(l => l.date));
      let streak = 0;
      // If today not done yet, start from yesterday (streak is still "active")
      let checkDate = todayStr;
      if (!doneDates.has(checkDate)) {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        checkDate = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
      }
      while (doneDates.has(checkDate)) {
        streak++;
        const prev = new Date(checkDate + 'T12:00:00');
        prev.setDate(prev.getDate() - 1);
        checkDate = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`;
      }
      return { current: streak, target: rule.threshold };
    }
    if (rule.ruleType === 'total_completions') {
      return { current: logs.filter(l => l.done).length, target: rule.threshold };
    }
    return { current: 0, target: rule.threshold };
  };

  const handleAddRule = () => {
    if (!rewardText.trim()) return;
    const newRule = { ruleType, threshold, rewardText: rewardText.trim() };
    const updatedRules = [...rules, newRule];
    onUpdate({
      ...habit,
      rewards: { ...habit.rewards, bigRewardsEnabled: true, bigRewardRules: updatedRules }
    });
    setShowAdd(false);
    setRewardText('');
  };

  const handleDeleteRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    onUpdate({
      ...habit,
      rewards: { ...habit.rewards, bigRewardRules: updatedRules }
    });
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gift className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-semibold text-slate-700">Belohnungen</h3>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="p-1 text-slate-400 hover:text-purple-600 rounded"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Existing rules */}
      {rules.length === 0 && !showAdd && (
        <p className="text-xs text-slate-400">Belohne dich für Fortschritte!</p>
      )}

      <div className="space-y-2">
        {rules.map((rule, i) => {
          const progress = getProgress(rule);
          const isUnlocked = progress.current >= progress.target;
          return (
            <div key={i} className={`p-3 rounded-lg border ${isUnlocked ? 'border-purple-200 bg-purple-50' : 'border-slate-100'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-slate-500">
                    {rule.ruleType === 'weekly_rate' ? `${rule.threshold}x diese Woche` :
                     rule.ruleType === 'streak' ? `${rule.threshold} Tage Streak` :
                     `${rule.threshold} mal insgesamt`}
                  </p>
                  <p className={`text-sm font-medium ${isUnlocked ? 'text-purple-700' : 'text-slate-700'}`}>
                    {isUnlocked && <Check className="w-3.5 h-3.5 inline mr-1" />}
                    {rule.rewardText}
                  </p>
                </div>
                <button onClick={() => handleDeleteRule(i)} className="p-1 text-slate-300 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${isUnlocked ? 'bg-purple-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, (progress.current / progress.target) * 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">{progress.current}/{progress.target}</p>
            </div>
          );
        })}
      </div>

      {/* Add new rule */}
      {showAdd && (
        <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-3">
          <div className="flex gap-1.5">
            {[
              { key: 'weekly_rate', label: 'Pro Woche' },
              { key: 'streak', label: 'Streak' },
              { key: 'total_completions', label: 'Insgesamt' }
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setRuleType(t.key)}
                className={`px-2 py-1 rounded text-xs font-medium ${
                  ruleType === t.key ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 border'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Wenn</span>
            <input
              type="number"
              min={1}
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              className="w-14 px-2 py-1 border rounded text-sm text-center"
            />
            <span className="text-xs text-slate-500">
              {ruleType === 'weekly_rate' ? 'x diese Woche' : ruleType === 'streak' ? 'Tage am Stück' : 'mal insgesamt'}
            </span>
          </div>
          <input
            type="text"
            value={rewardText}
            onChange={e => setRewardText(e.target.value)}
            placeholder="Belohnung: z.B. Netflix-Abend"
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
          <div className="flex gap-2">
            <button onClick={handleAddRule} className="flex-1 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg">
              Hinzufügen
            </button>
            <button onClick={() => setShowAdd(false)} className="px-3 py-2 bg-white border text-slate-600 text-sm rounded-lg">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Unlocked celebration */}
      {unlockedReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setUnlockedReward(null)}>
          <div className="bg-white rounded-2xl p-6 text-center max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Gift className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Belohnung freigeschaltet!</h3>
            <p className="text-slate-600">{unlockedReward}</p>
            <button onClick={() => setUnlockedReward(null)} className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-xl font-medium">
              Nice!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
